/**
 * THE ANSWERING ANSWERS — plan-015 AGC-API (gaps G4, G8, G12; design §28.3.14, §28.5.4; ADRs 0044, 0047;
 * contract IC-DG-2).
 *
 * What a VoiceAgent does when it is closed or every line is busy, its own time zone and its special dates are
 * ANSWERS: the wizard and the edit send them, the api stores them under `config_document.draft` exactly as they
 * travelled (snake_case), and the factory turns them into the version's `openingHours.timeZone`,
 * `openingHours.exception[]`, `afterHours`, `overflow` and `transferTo`. Nothing outside a version reads them.
 *
 * ```
 *   wire / draft key        shape                                         becomes
 *   ────────────────────    ────────────────────────────────────────────  ──────────────────────────────────────
 *   time_zone               IANA zone (the workspace's when unstated)      openingHours.timeZone
 *   special_date[]          { date, label (0–40), interval[0..3] }         openingHours.exception[] (past dropped)
 *   after_hours_choice      closed | message | forward                     afterHours
 *   busy_choice             queue | message | forward                      overflow
 *   longest_wait_minute     1 | 2 | 3 | 5, only with busy_choice = queue   overflow.maxWaitMs = minutes × 60 000
 *   forward_to              E.164, exactly when a choice is forward         transferTo (null otherwise)
 * ```
 *
 * TWO GROUPS. The HOURS answers (`time_zone`, `special_date`) are always written: an omitted zone is the
 * workspace's, written explicitly ({@link TIME_ZONE_WHEN_UNSTATED}, owner answer 12), and omitted dates are none.
 * The CHOICE answers (`after_hours_choice`, `busy_choice`, and with them `longest_wait_minute`, `forward_to`) are
 * stated together or not at all: a request that states neither choice is an API client older than this release,
 * and the factory applies today's documented platform policy for it (design §28.3.14 — the existing contract for a
 * missing optional answer, not a new fallback). One choice without the other is refused, naming the missing one.
 *
 * WHERE A FORWARD MAY GO (ADR 0047, owner answer 14). {@link forwardDestinationRefusalOf} is the reviewed
 * destination rule the SIP trunks dial under (`destinationVerdict` over `REVIEWED_DESTINATION_PREFIX`: GR at
 * launch, emergency, short-code and premium-rate ranges refused). The api applies it when the number is saved, and
 * the agent runtime again when it dials, because an immutable version outlives a shrinking list. Whether the number
 * is one of WeTalk's own is a database fact the caller checks beside it.
 *
 * Pure. Every reader throws `ValidationError` naming the field (`special_date[2].interval`), never substitutes.
 */
import { isBillingTimeZone } from "../billing/billing-timezone.js";
import { ValidationError } from "../error/taxonomy.js";
import { REVIEWED_DESTINATION_COUNTRY, REVIEWED_DESTINATION_PREFIX } from "../telephony/sip-trunk/destination-prefix.js";
import { destinationVerdict } from "../telephony/sip-trunk/destination.js";
import { isE164 } from "../telephony/sip-trunk/wire-number.js";
// ── the choices (the design's own) ───────────────────────────────────────────────────────────
export const AFTER_HOURS_CHOICE = ["closed", "message", "forward"];
export const BUSY_CHOICE = ["queue", "message", "forward"];
/** The design's longest-wait choices, in minutes (a named product constant, design §28.7). */
export const LONGEST_WAIT_MINUTE = [1, 2, 3, 5];
/** At most three opening intervals on a special date — the design's editor (design §28.7). */
export const SPECIAL_DATE_INTERVAL_MAXIMUM = 3;
/** A special date's label, at most this many characters. */
export const SPECIAL_DATE_LABEL_MAXIMUM = 40;
/** How many special dates one version carries, at most (the OpenAPI `maxItems`). */
export const SPECIAL_DATE_MAXIMUM = 60;
/**
 * Owner answer 12: a VoiceAgent's zone defaults to its workspace's. The api WRITES that zone into the draft when a
 * request omits it — a named product default for a person's answer, never read later from the workspace.
 */
export const TIME_ZONE_WHEN_UNSTATED = "workspace";
/** The platform phrase keys each behaviour speaks (every pack carries them, `packages/lang/src/platform-phrase.ts`). */
export const ANSWERING_PHRASE_KEY = Object.freeze({
    afterHoursClosed: "after_hours.closed",
    afterHoursTakeMessage: "after_hours.take_message",
    busyQueueHold: "overflow.queue_hold",
    busyTakeMessage: "overflow.take_message",
});
/** The snake_case keys the readers accept, and nothing else of the answering group. */
export const ANSWERING_ANSWER_KEY = [
    "time_zone",
    "special_date",
    "after_hours_choice",
    "busy_choice",
    "longest_wait_minute",
    "forward_to",
];
// ── readers ──────────────────────────────────────────────────────────────────────────────────
function refusal(message, field) {
    return new ValidationError({ code: "validation_failed", message, field });
}
const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d$/;
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Whether `value` is a real `YYYY-MM-DD` date (no 2026-02-30). */
export function isCalendarDate(value) {
    const matched = CALENDAR_DATE.exec(value);
    if (matched === null)
        return false;
    const year = Number(matched[1]);
    const month = Number(matched[2]);
    const day = Number(matched[3]);
    const at = new Date(Date.UTC(year, month - 1, day));
    return at.getUTCFullYear() === year && at.getUTCMonth() === month - 1 && at.getUTCDate() === day;
}
/** An IANA zone, or `time_zone_invalid` (400) naming `field` — the zone check billing's timezone uses. */
export function timeZoneAnswerOf(value, field = "time_zone") {
    if (typeof value !== "string" || !isBillingTimeZone(value)) {
        throw new ValidationError({
            code: "time_zone_invalid",
            message: `${JSON.stringify(value)} is not a time zone. Choose one from the list, for example Europe/Athens.`,
            field,
        });
    }
    return value;
}
function intervalOf(value, field) {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        throw refusal("Each period needs an opening and a closing time, such as 12:00 to 15:00.", field);
    }
    const opensAt = value["opens_at"];
    const closesAt = value["closes_at"];
    if (typeof opensAt !== "string" || !TIME_OF_DAY.test(opensAt) || typeof closesAt !== "string" || !TIME_OF_DAY.test(closesAt)) {
        throw refusal("Write each time as HH:MM, such as 12:00 to 15:00.", field);
    }
    return { opensAt, closesAt };
}
/**
 * `special_date` — a list of at most {@link SPECIAL_DATE_MAXIMUM} dates, each once, each with a label of at most
 * {@link SPECIAL_DATE_LABEL_MAXIMUM} characters (absent is none) and 0–3 periods under the week's rules: a period
 * that closes before it opens runs past midnight, and `00:00`–`00:00` is the whole day and stands alone.
 */
export function specialDateAnswerOf(value, field = "special_date") {
    if (!Array.isArray(value)) {
        throw refusal("Special dates must be a list of dates, each closed or with its own hours.", field);
    }
    if (value.length > SPECIAL_DATE_MAXIMUM) {
        throw refusal(`At most ${String(SPECIAL_DATE_MAXIMUM)} special dates can be set. Remove some that have passed.`, field);
    }
    const seen = new Set();
    return value.map((one, index) => {
        const at = `${field}[${String(index)}]`;
        if (typeof one !== "object" || one === null || Array.isArray(one)) {
            throw refusal("Each special date needs a date and, unless it is closed, its hours.", at);
        }
        const shaped = one;
        const date = shaped["date"];
        if (typeof date !== "string" || !isCalendarDate(date)) {
            throw refusal("Write each special date as a calendar date, such as 2026-12-25.", `${at}.date`);
        }
        if (seen.has(date)) {
            throw refusal(`${date} is set twice. Keep one entry for each date.`, `${at}.date`);
        }
        seen.add(date);
        const rawLabel = shaped["label"];
        if (rawLabel !== undefined && rawLabel !== null && typeof rawLabel !== "string") {
            throw refusal("A special date's name must be text, such as Christmas.", `${at}.label`);
        }
        const label = typeof rawLabel === "string" ? rawLabel.trim() : "";
        if (label.length > SPECIAL_DATE_LABEL_MAXIMUM) {
            throw refusal(`A special date's name can be at most ${String(SPECIAL_DATE_LABEL_MAXIMUM)} characters.`, `${at}.label`);
        }
        const rawInterval = shaped["interval"];
        if (!Array.isArray(rawInterval)) {
            throw refusal("Say whether the date is closed (no periods) or list its own hours.", `${at}.interval`);
        }
        if (rawInterval.length > SPECIAL_DATE_INTERVAL_MAXIMUM) {
            throw refusal(`A special date can have at most ${String(SPECIAL_DATE_INTERVAL_MAXIMUM)} periods of its own hours.`, `${at}.interval`);
        }
        const interval = rawInterval.map((period, at2) => intervalOf(period, `${at}.interval[${String(at2)}]`));
        for (const [periodAt, period] of interval.entries()) {
            const wholeDay = period.opensAt === "00:00" && period.closesAt === "00:00";
            if (period.opensAt === period.closesAt && !wholeDay) {
                throw refusal(`${period.opensAt} to ${period.closesAt} opens and closes at the same time. For the whole day write 00:00 to 00:00.`, `${at}.interval[${String(periodAt)}]`);
            }
            if (wholeDay && interval.length > 1) {
                throw refusal("A date open the whole day (00:00 to 00:00) has no other period.", `${at}.interval`);
            }
            const repeated = interval.findIndex((other) => other.opensAt === period.opensAt && other.closesAt === period.closesAt);
            if (repeated !== periodAt) {
                throw refusal(`${period.opensAt} to ${period.closesAt} is listed twice on ${date}.`, `${at}.interval[${String(periodAt)}]`);
            }
        }
        return Object.freeze({ date, label, interval: Object.freeze(interval) });
    });
}
/** `forward_to` in its shape (E.164). Where it may go is {@link forwardDestinationRefusalOf}'s. */
export function forwardToAnswerOf(value, field = "forward_to") {
    if (typeof value !== "string" || !isE164(value.trim())) {
        throw refusal("Write the phone number to forward to in international form, starting with +30.", field);
    }
    return value.trim();
}
function oneOf(value, allowed, message, field) {
    if (!allowed.includes(value))
        throw refusal(message, field);
    return value;
}
const absent = (value) => value === undefined || value === null;
/**
 * The choice group of `raw` (a request body or a stored draft), or `null` when it states none of it. One choice
 * without the other, a longest wait without `queue` (or `queue` without one), and a forward number without a
 * forward choice (or a forward choice without one) are refused, naming the field.
 */
export function answeringChoiceAnswerOf(raw) {
    const afterHours = raw["after_hours_choice"];
    const busy = raw["busy_choice"];
    const wait = raw["longest_wait_minute"];
    const forward = raw["forward_to"];
    if (absent(afterHours) && absent(busy)) {
        if (!absent(wait))
            throw refusal("A longest wait is set only when callers queue while every line is busy.", "longest_wait_minute");
        if (!absent(forward))
            throw refusal("A number to forward to is set only when a choice is to forward the call.", "forward_to");
        return null;
    }
    if (absent(afterHours)) {
        throw refusal("Choose what happens when you are closed: say you are closed, take a message, or forward the call.", "after_hours_choice");
    }
    if (absent(busy)) {
        throw refusal("Choose what happens when every line is busy: queue, take a message, or forward the call.", "busy_choice");
    }
    const afterHoursChoice = oneOf(afterHours, AFTER_HOURS_CHOICE, "When you are closed, the agent can say you are closed, take a message, or forward the call.", "after_hours_choice");
    const busyChoice = oneOf(busy, BUSY_CHOICE, "When every line is busy, callers can queue, leave a message, or be forwarded.", "busy_choice");
    let longestWaitMinute = null;
    if (busyChoice === "queue") {
        longestWaitMinute = oneOf(wait, LONGEST_WAIT_MINUTE, "Choose the longest a caller waits in the queue: 1, 2, 3 or 5 minutes.", "longest_wait_minute");
    }
    else if (!absent(wait)) {
        throw refusal("A longest wait is set only when callers queue while every line is busy.", "longest_wait_minute");
    }
    const forwards = afterHoursChoice === "forward" || busyChoice === "forward";
    let forwardTo = null;
    if (forwards) {
        if (absent(forward))
            throw refusal("Enter the phone number to forward calls to.", "forward_to");
        forwardTo = forwardToAnswerOf(forward);
    }
    else if (!absent(forward)) {
        throw refusal("A number to forward to is set only when a choice is to forward the call.", "forward_to");
    }
    return Object.freeze({ afterHoursChoice, busyChoice, longestWaitMinute, forwardTo });
}
/**
 * Both groups as `raw` states them: the zone and the dates when present, the choices when stated. Used for a
 * request body (the caller then writes the zone it decided for an absent one) and for a stored draft.
 */
export function statedAnsweringAnswerOf(raw) {
    return {
        timeZone: absent(raw["time_zone"]) ? null : timeZoneAnswerOf(raw["time_zone"]),
        specialDate: absent(raw["special_date"]) ? null : specialDateAnswerOf(raw["special_date"]),
        choice: answeringChoiceAnswerOf(raw),
    };
}
/**
 * IC-DG-2: every answering answer of `raw`, the zone defaulting to `workspaceTimeZone` (answer 12) and the dates
 * to none; the choices are required here (`after_hours_choice` is named when they are absent).
 */
export function answeringAnswerOf(raw, workspaceTimeZone) {
    if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
        throw refusal("The answers about closed hours and busy lines could not be read.", "after_hours_choice");
    }
    const stated = statedAnsweringAnswerOf(raw);
    if (stated.choice === null) {
        throw refusal("Choose what happens when you are closed: say you are closed, take a message, or forward the call.", "after_hours_choice");
    }
    return Object.freeze({
        timeZone: stated.timeZone ?? timeZoneAnswerOf(workspaceTimeZone),
        specialDate: stated.specialDate ?? [],
        ...stated.choice,
    });
}
// ── writers ──────────────────────────────────────────────────────────────────────────────────
/** The snake_case draft keys of the hours group. */
export function hoursDraftOf(hours) {
    return {
        time_zone: hours.timeZone,
        special_date: hours.specialDate.map((one) => ({
            date: one.date,
            label: one.label,
            interval: one.interval.map((period) => ({ opens_at: period.opensAt, closes_at: period.closesAt })),
        })),
    };
}
/** The snake_case draft keys of the choice group; nothing when it is unstated. */
export function choiceDraftOf(choice) {
    if (choice === null)
        return {};
    return {
        after_hours_choice: choice.afterHoursChoice,
        busy_choice: choice.busyChoice,
        longest_wait_minute: choice.longestWaitMinute,
        forward_to: choice.forwardTo,
    };
}
/**
 * IC-DG-2: the choices, one-to-one onto the document's behaviours (design §28.5.4).
 *
 *   closed → closed_message(after_hours.closed) · message → message_taken(after_hours.take_message) ·
 *   forward → transferred(forward_to);
 *   queue → queued(overflow.queue_hold, minutes × 60 000) · message → message_taken(overflow.take_message) ·
 *   forward → transferred(forward_to); transferTo := forward_to when a choice forwards, else null.
 */
export function behaviourOf(answer) {
    const forwardTo = () => {
        if (answer.forwardTo === null)
            throw new Error("a forward choice carries no forward_to (answeringChoiceAnswerOf refuses it)");
        return answer.forwardTo;
    };
    const afterHours = answer.afterHoursChoice === "closed"
        ? { kind: "closed_message", phraseKey: ANSWERING_PHRASE_KEY.afterHoursClosed }
        : answer.afterHoursChoice === "message"
            ? { kind: "message_taken", promptPhraseKey: ANSWERING_PHRASE_KEY.afterHoursTakeMessage }
            : { kind: "transferred", transferTo: forwardTo() };
    let overflow;
    if (answer.busyChoice === "queue") {
        if (answer.longestWaitMinute === null)
            throw new Error("a queue choice carries no longest_wait_minute (answeringChoiceAnswerOf refuses it)");
        overflow = { kind: "queued", queuePhraseKey: ANSWERING_PHRASE_KEY.busyQueueHold, maxWaitMs: answer.longestWaitMinute * 60_000 };
    }
    else if (answer.busyChoice === "message") {
        overflow = { kind: "message_taken", promptPhraseKey: ANSWERING_PHRASE_KEY.busyTakeMessage };
    }
    else {
        overflow = { kind: "transferred", transferTo: forwardTo() };
    }
    const forwards = answer.afterHoursChoice === "forward" || answer.busyChoice === "forward";
    return Object.freeze({ afterHours, overflow, transferTo: forwards ? forwardTo() : null });
}
/** The calendar date at `atMs` in `timeZone`, `YYYY-MM-DD`. */
export function calendarDateIn(timeZone, atMs) {
    const part = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(atMs));
    const of = (kind) => {
        const found = part.find((one) => one.type === kind)?.value;
        if (found === undefined)
            throw new Error(`Intl gave no ${kind} for ${timeZone}`);
        return found;
    };
    return `${of("year")}-${of("month")}-${of("day")}`;
}
/**
 * IC-DG-2: the special dates as `OpeningHours.exception`, the ones before `buildDate` dropped (ADR 0044 §3: dates
 * already past at build time are not carried), in date order.
 *
 * The document's schema requires a non-empty `label`, and a special date's name is optional (0–40 characters), so
 * an unnamed date is named by its own date (`2026-12-24`) — a stated, deterministic value the runtime never speaks
 * — and {@link answeringAnswerOfDocument} reads a label equal to its date back as unnamed.
 */
export function exceptionOf(answer, buildDate) {
    return answer.specialDate
        .filter((one) => one.date >= buildDate)
        .map((one) => ({
        date: one.date,
        label: one.label === "" ? one.date : one.label,
        interval: one.interval.map((period) => ({ ...period })),
    }))
        .sort((one, other) => (one.date < other.date ? -1 : one.date > other.date ? 1 : 0));
}
// ── reading a built document back ────────────────────────────────────────────────────────────
function recordOf(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? value : null;
}
/** The answers a built `config_document` answers with. Never guesses: what it cannot read is `null`. */
export function answeringAnswerOfDocument(configDocument) {
    const document = recordOf(configDocument);
    const opening = recordOf(document?.["openingHours"]);
    let hours = null;
    const zone = opening?.["timeZone"];
    const exception = opening?.["exception"];
    if (typeof zone === "string" && Array.isArray(exception)) {
        const specialDate = [];
        let readable = true;
        for (const one of exception) {
            const shaped = recordOf(one);
            const date = shaped?.["date"];
            const label = shaped?.["label"];
            const interval = shaped?.["interval"];
            if (typeof date !== "string" || typeof label !== "string" || !Array.isArray(interval)) {
                readable = false;
                break;
            }
            const period = [];
            for (const each of interval) {
                const opensAt = recordOf(each)?.["opensAt"];
                const closesAt = recordOf(each)?.["closesAt"];
                if (typeof opensAt !== "string" || typeof closesAt !== "string") {
                    readable = false;
                    break;
                }
                period.push({ opensAt, closesAt });
            }
            if (!readable)
                break;
            specialDate.push({ date, label: label === date ? "" : label, interval: period });
        }
        if (readable)
            hours = { timeZone: zone, specialDate };
    }
    const afterHours = recordOf(document?.["afterHours"]);
    const overflow = recordOf(document?.["overflow"]);
    const transferTo = document?.["transferTo"];
    const waitMs = overflow?.["kind"] === "queued" ? overflow["maxWaitMs"] : null;
    const busyWaitSecond = typeof waitMs === "number" && Number.isSafeInteger(waitMs) && waitMs >= 0 ? Math.round(waitMs / 1000) : null;
    const afterHoursChoice = afterHours?.["kind"] === "closed_message"
        ? "closed"
        : afterHours?.["kind"] === "message_taken"
            ? "message"
            : afterHours?.["kind"] === "transferred"
                ? "forward"
                : null;
    const busyChoice = overflow?.["kind"] === "queued"
        ? "queue"
        : overflow?.["kind"] === "message_taken"
            ? "message"
            : overflow?.["kind"] === "transferred"
                ? "forward"
                : null;
    let longestWaitMinute = null;
    let choiceReadable = afterHoursChoice !== null && busyChoice !== null;
    if (busyChoice === "queue") {
        const minute = typeof waitMs === "number" ? waitMs / 60_000 : Number.NaN;
        if (LONGEST_WAIT_MINUTE.includes(minute))
            longestWaitMinute = minute;
        else
            choiceReadable = false;
    }
    const forwards = afterHoursChoice === "forward" || busyChoice === "forward";
    const forwardTo = forwards && typeof transferTo === "string" && transferTo !== "" ? transferTo : null;
    if (forwards && forwardTo === null)
        choiceReadable = false;
    const choice = choiceReadable && afterHoursChoice !== null && busyChoice !== null
        ? { afterHoursChoice, busyChoice, longestWaitMinute, forwardTo }
        : null;
    return { hours, choice, busyWaitSecond };
}
// ── where a forward may go ───────────────────────────────────────────────────────────────────
/**
 * The reviewed destination rule for a forward (ADR 0047 §1): `null` when `e164` may be forwarded to, else the rule
 * that refuses it. The same data a SIP trunk dials under, with every reviewed country allowed.
 */
export function forwardDestinationRefusalOf(e164) {
    const verdict = destinationVerdict(e164, REVIEWED_DESTINATION_COUNTRY, REVIEWED_DESTINATION_PREFIX);
    return verdict.allowed ? null : verdict.rule;
}
/** The customer's sentence for each refusal of {@link forwardDestinationRefusalOf}, and for one of WeTalk's numbers. */
export function forwardDestinationRefusalMessageOf(rule) {
    switch (rule) {
        case "not_e164":
            return "That is not a full phone number. Write it in international form, such as +30 69 1234 5678.";
        case "country_unreviewed":
        case "country_not_allowed":
            return "Calls can be forwarded only to phone numbers in Greece (+30) for now. Enter a Greek number.";
        case "premium_rate":
            return "Calls cannot be forwarded to a premium-rate or special-tariff number. Enter an ordinary phone number.";
        case "emergency_or_short":
            return "Calls cannot be forwarded to an emergency number or a short code. Enter an ordinary phone number.";
        case "wetalk_number":
            return "That number already answers through wetalk, so forwarding to it would send callers back to an agent. Enter another phone.";
    }
}
//# sourceMappingURL=answering-answer.js.map